import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '@shared/supabase/client';
import { useSupabaseQuery } from '@shared/supabase/hooks';
import { Input, Skeleton, SectionErrorBoundary } from '@shared/ui';
import { currentLocale } from '@shared/i18n';

interface CreatorRow {
  id: string; user_id: string | null; handle: string | null; type: string | null; city: string | null;
  followers: number | null; engagement_rate: number | null; posts_total: number | null; stories_total: number | null;
  last_post_scrape_at: string | null; verified: boolean | null; claimed: boolean | null;
}

type Sort = 'followers' | 'er' | 'stale';
const nf = (n: number | null | undefined) => (n == null ? '—' : n >= 1e6 ? `${(n / 1e6).toFixed(1)}M` : n >= 1e3 ? `${Math.round(n / 1e3)}K` : String(n));
const fmtDate = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString(currentLocale()) : null);

// Scrape-freshness colour so the stalest creators are visible at a glance.
function staleColor(iso: string | null): string {
  if (!iso) return '#e74c3c';
  const days = (Date.now() - new Date(iso).getTime()) / 86_400_000;
  return days > 14 ? '#e74c3c' : days > 7 ? '#f1c40f' : '#2ecc71';
}

// Last login (claimed creators only) — green if within a week, muted date otherwise, '—' if none.
function lastSeen(iso: string | null): { txt: string; color: string } {
  if (!iso) return { txt: '—', color: '#556' };
  const days = (Date.now() - new Date(iso).getTime()) / 86_400_000;
  const txt = days < 1 ? 'сегодня' : days < 2 ? 'вчера' : days < 8 ? `${Math.floor(days)} дн назад`
    : new Date(iso).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: '2-digit' });
  return { txt, color: days < 8 ? '#5fd17a' : '#8a93a6' };
}

/** Clean creator list. `onlyRegistered` → only creators who signed up as a creator
 *  (have a user_id) = the account view under Accounts. Without it = the full scraped
 *  catalog (used in the Catalog tab, alongside Discovery ops). */
export default function CreatorsList({ onlyRegistered = false }: { onlyRegistered?: boolean }) {
  const { t } = useTranslation('admin');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<Sort>('followers');
  const [open, setOpen] = useState<CreatorRow | null>(null);

  const { data, isLoading, error } = useSupabaseQuery<CreatorRow[]>(
    ['admin-creators-list', search, sort, onlyRegistered],
    async () => {
      let q = supabase.from('v_admin_ig_creators')
        .select('id, user_id, handle, type, city, followers, engagement_rate, posts_total, stories_total, last_post_scrape_at, verified, claimed')
        .limit(500);
      if (onlyRegistered) q = q.not('user_id', 'is', null);
      if (sort === 'followers') q = q.order('followers', { ascending: false, nullsFirst: false });
      else if (sort === 'er') q = q.order('engagement_rate', { ascending: false, nullsFirst: false });
      else q = q.order('last_post_scrape_at', { ascending: true, nullsFirst: true });
      if (search.trim()) { const term = search.trim().replace(/[*,()]/g, ''); q = q.ilike('handle', `*${term}*`); }
      return q;
    },
  );
  const rows = data ?? [];

  // Last sign-in per claimed creator (auth.users isn't client-readable → admin RPC).
  const { data: seenData } = useSupabaseQuery<{ user_id: string; last_seen: string | null }[]>(
    ['admin-creator-last-seen'], async () => supabase.rpc('admin_creator_last_seen'));
  const seen = useMemo(() => new Map((seenData ?? []).map((r) => [r.user_id, r.last_seen])), [seenData]);

  return (
    <SectionErrorBoundary name="admin-creators-list">
      <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
        <Input placeholder={t('cr.search', { defaultValue: 'Поиск по @нику…' }) as string} value={search} onChange={(e) => setSearch(e.target.value)} style={{ minWidth: 240 }} />
        <div style={{ display: 'flex', gap: 6 }}>
          {([
            { v: 'followers', label: t('cr.sortFollowers', { defaultValue: 'По подписчикам' }) },
            { v: 'er', label: t('cr.sortEr', { defaultValue: 'По ER' }) },
            { v: 'stale', label: t('cr.sortStale', { defaultValue: 'Устаревшие' }) },
          ] as const).map((s) => {
            const on = sort === s.v;
            return <button key={s.v} type="button" onClick={() => setSort(s.v)}
              style={{ padding: '6px 12px', borderRadius: 6, cursor: 'pointer', fontFamily: 'inherit', fontSize: 'var(--fs-small)',
                border: on ? '1px solid transparent' : '1px solid rgba(15,45,100,0.5)', background: on ? 'rgba(74,158,255,0.16)' : 'transparent', color: on ? '#bcd9ff' : '#9aa' }}>{s.label as string}</button>;
          })}
        </div>
      </div>

      {isLoading && <Skeleton height={240} />}
      {error && <div style={{ color: '#e74c3c' }}>{String((error as Error)?.message ?? error)}</div>}
      {!isLoading && rows.length === 0 && <div style={{ color: '#888', padding: 24, textAlign: 'center' }}>{t('cr.empty', { defaultValue: 'Ничего не найдено.' })}</div>}

      {rows.length > 0 && (
        <div style={{ overflowX: 'auto', border: '1px solid rgba(15,45,100,0.4)', borderRadius: 8 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--fs-body)' }}>
            <thead style={{ background: 'rgba(15,45,100,0.2)' }}>
              <tr>
                <th style={th}>{t('cr.creator', { defaultValue: 'Креатор' })}</th>
                <th style={{ ...th, textAlign: 'right' }}>{t('cr.followers', { defaultValue: 'Подписчики' })}</th>
                <th style={{ ...th, textAlign: 'right' }}>ER</th>
                <th style={{ ...th, textAlign: 'right' }}>{t('cr.posts', { defaultValue: 'Посты' })}</th>
                <th style={th}>{t('cr.lastScrape', { defaultValue: 'Посл. скрейп' })}</th>
                <th style={th}>{t('cr.lastSeen', { defaultValue: 'Был онлайн' })}</th>
                <th style={th}>{t('cr.type', { defaultValue: 'Тип' })}</th>
                <th style={th} />
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.id} onClick={() => setOpen(c)} style={{ borderTop: '1px solid rgba(15,45,100,0.3)', cursor: 'pointer' }}>
                  <td style={{ ...td, color: '#dde' }}>
                    @{c.handle}
                    {c.claimed && <span style={{ marginLeft: 6, fontSize: 'var(--fs-caption)', color: '#2ecc71' }}>✓</span>}
                    {c.city && <span style={{ marginLeft: 6, fontSize: 'var(--fs-caption)', color: '#7f8aa3' }}>{c.city}</span>}
                  </td>
                  <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{nf(c.followers)}</td>
                  <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{c.engagement_rate != null ? `${Number(c.engagement_rate).toFixed(1)}%` : '—'}</td>
                  <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{nf(c.posts_total)}</td>
                  <td style={td}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ width: 8, height: 8, borderRadius: '50%', background: staleColor(c.last_post_scrape_at) }} />
                      {fmtDate(c.last_post_scrape_at) ?? <span style={{ color: '#666' }}>{t('cr.never', { defaultValue: 'никогда' })}</span>}
                    </span>
                  </td>
                  <td style={{ ...td, color: lastSeen(c.user_id ? seen.get(c.user_id) ?? null : null).color, whiteSpace: 'nowrap' }}>
                    {lastSeen(c.user_id ? seen.get(c.user_id) ?? null : null).txt}
                  </td>
                  <td style={{ ...td, color: '#8a93a6', fontSize: 'var(--fs-small)' }}>{c.type ?? '—'}</td>
                  <td style={{ ...td, color: '#7f8aa3' }}>›</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {open && <CreatorDrawer c={open} onClose={() => setOpen(null)} />}
    </SectionErrorBoundary>
  );
}

function CreatorDrawer({ c, onClose }: { c: CreatorRow; onClose: () => void }) {
  const { t } = useTranslation('admin');
  const stat = (label: string, value: string) => (
    <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(15,45,100,0.4)', borderRadius: 10, padding: 12 }}>
      <div style={{ fontSize: 'var(--fs-caption)', color: '#8a93a6' }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 600, marginTop: 4 }}>{value}</div>
    </div>
  );
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 50, background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'flex-end' }}>
      <aside onClick={(e) => e.stopPropagation()} style={{ width: 'min(440px, 94vw)', height: '100%', overflowY: 'auto', background: 'var(--bg-elev,#12151c)', borderLeft: '1px solid rgba(15,45,100,0.5)', padding: 20, display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0 }}>
            <a href={`https://instagram.com/${c.handle}`} target="_blank" rel="noreferrer" style={{ color: '#dde' }}>@{c.handle}</a>
          </h3>
          <button type="button" onClick={onClose} style={{ background: 'none', border: 0, color: '#9aa', fontSize: 22, cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>
        <div style={{ display: 'flex', gap: 8, fontSize: 'var(--fs-small)' }}>
          {c.claimed ? <span style={{ color: '#2ecc71' }}>✓ {t('cr.claimed', { defaultValue: 'Подтверждён (есть аккаунт)' })}</span> : <span style={{ color: '#8a93a6' }}>{t('cr.unclaimed', { defaultValue: 'Не подтверждён' })}</span>}
          {c.verified && <span style={{ color: '#4a9eff' }}>· IG verified</span>}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 10 }}>
          {stat(t('cr.followers', { defaultValue: 'Подписчики' }) as string, nf(c.followers))}
          {stat('ER', c.engagement_rate != null ? `${Number(c.engagement_rate).toFixed(1)}%` : '—')}
          {stat(t('cr.posts', { defaultValue: 'Посты' }) as string, nf(c.posts_total))}
          {stat(t('cr.stories', { defaultValue: 'Сторис' }) as string, nf(c.stories_total))}
        </div>
        <div style={{ fontSize: 'var(--fs-small)', color: '#8a93a6' }}>
          {t('cr.lastScrape', { defaultValue: 'Посл. скрейп' })}: {fmtDate(c.last_post_scrape_at) ?? t('cr.never', { defaultValue: 'никогда' })}
        </div>
        <div style={{ fontSize: 'var(--fs-caption)', color: '#666' }}>{t('cr.opsHint', { defaultValue: 'Скрейп, удаление и триаж новых хэндлов — во вкладке «Discovery».' })}</div>
      </aside>
    </div>
  );
}

const th: React.CSSProperties = { padding: 10, textAlign: 'left' };
const td: React.CSSProperties = { padding: 10 };
